import type {
  Branch,
  CartItem,
  ChatMessage,
  DiningTable,
  MenuCategory,
  MenuItem,
  Order,
  Restaurant,
  ServiceRequest,
} from '../types'

export const demoRestaurant: Restaurant = {
  id: 'rest-ava',
  name: 'AVA XIN Delight',
  slug: 'ava-xin-delight',
  tagline: 'Modern comfort, made for sharing.',
  description: 'A warm all-day dining experience with vibrant plates, comforting favourites and a table-first approach to hospitality.',
  logoUrl: 'https://picsum.photos/seed/ava-logo/240/240',
  coverUrl: 'https://picsum.photos/seed/ava-cover/1600/900',
  primaryColor: '#101419',
  accentColor: '#8fb9b0',
  currency: 'BD',
  orderingEnabled: true,
}

export const demoBranch: Branch = {
  id: 'branch-seef',
  restaurantId: demoRestaurant.id,
  name: 'Seef Demo Branch',
  slug: 'seef-demo',
}

export const demoCategories: MenuCategory[] = [
  { id: 'cat-signature', restaurantId: demoRestaurant.id, name: 'Signature', sortOrder: 1, active: true },
  { id: 'cat-mains', restaurantId: demoRestaurant.id, name: 'Mains', sortOrder: 2, active: true },
  { id: 'cat-sharing', restaurantId: demoRestaurant.id, name: 'To Share', sortOrder: 3, active: true },
  { id: 'cat-drinks', restaurantId: demoRestaurant.id, name: 'Drinks', sortOrder: 4, active: true },
  { id: 'cat-dessert', restaurantId: demoRestaurant.id, name: 'Dessert', sortOrder: 5, active: true },
]

export const demoMenuItems: MenuItem[] = [
  {
    id: 'item-burger', restaurantId: demoRestaurant.id, categoryId: 'cat-signature', name: 'AVA Signature Burger',
    description: 'Smashed beef, aged cheddar, caramelised onion, house sauce and crisp lettuce.',
    price: 4.9, imageUrl: 'https://picsum.photos/seed/ava-burger/900/700', featured: true, available: true, sortOrder: 1,
  },
  {
    id: 'item-pasta', restaurantId: demoRestaurant.id, categoryId: 'cat-signature', name: 'Truffle Rigatoni',
    description: 'Creamy parmesan sauce, roasted mushrooms, truffle and cracked black pepper.',
    price: 5.8, imageUrl: 'https://picsum.photos/seed/ava-pasta/900/700', featured: true, available: true, sortOrder: 2,
  },
  {
    id: 'item-chicken', restaurantId: demoRestaurant.id, categoryId: 'cat-mains', name: 'Charred Lemon Chicken',
    description: 'Herb-marinated chicken, lemon jus, roasted potatoes and seasonal greens.',
    price: 6.2, imageUrl: 'https://picsum.photos/seed/ava-chicken/900/700', featured: false, available: true, sortOrder: 3,
  },
  {
    id: 'item-salmon', restaurantId: demoRestaurant.id, categoryId: 'cat-mains', name: 'Miso Glazed Salmon',
    description: 'Glazed salmon, jasmine rice, pickled cucumber and sesame greens.',
    price: 7.4, imageUrl: 'https://picsum.photos/seed/ava-salmon/900/700', featured: true, available: true, sortOrder: 4,
  },
  {
    id: 'item-fries', restaurantId: demoRestaurant.id, categoryId: 'cat-sharing', name: 'Parmesan Fries',
    description: 'Crisp fries, parmesan, rosemary salt and truffle mayo.',
    price: 2.6, imageUrl: 'https://picsum.photos/seed/ava-fries/900/700', featured: false, available: true, sortOrder: 5,
  },
  {
    id: 'item-flatbread', restaurantId: demoRestaurant.id, categoryId: 'cat-sharing', name: 'Roasted Pepper Flatbread',
    description: 'Whipped feta, roasted peppers, basil oil and toasted seeds.',
    price: 3.8, imageUrl: 'https://picsum.photos/seed/ava-flatbread/900/700', featured: false, available: true, sortOrder: 6,
  },
  {
    id: 'item-cola', restaurantId: demoRestaurant.id, categoryId: 'cat-drinks', name: 'Cola',
    description: 'Chilled soft drink.', price: 1.2, imageUrl: 'https://picsum.photos/seed/ava-cola/900/700', featured: false, available: true, sortOrder: 7,
  },
  {
    id: 'item-sparkling', restaurantId: demoRestaurant.id, categoryId: 'cat-drinks', name: 'Sparkling Water',
    description: 'Premium sparkling mineral water.', price: 1.5, imageUrl: 'https://picsum.photos/seed/ava-water/900/700', featured: false, available: true, sortOrder: 8,
  },
  {
    id: 'item-cake', restaurantId: demoRestaurant.id, categoryId: 'cat-dessert', name: 'Warm Chocolate Cake',
    description: 'Dark chocolate cake, vanilla cream and salted caramel.',
    price: 3.9, imageUrl: 'https://picsum.photos/seed/ava-cake/900/700', featured: true, available: true, sortOrder: 9,
  },
]

const demoTableOverrides: Record<number, Partial<DiningTable>> = {
  4: { status: 'ready', guests: 3, elapsedSeconds: 68, sessionId: 'session-4' },
  7: { status: 'bill_requested', guests: 2, elapsedSeconds: 132, sessionId: 'session-7' },
  9: { status: 'assistance', guests: 5, elapsedSeconds: 224, sessionId: 'session-9' },
  12: { status: 'new_order', guests: 4, elapsedSeconds: 38, sessionId: 'session-12' },
  2: { status: 'browsing', guests: 2, elapsedSeconds: 0, sessionId: 'session-2' },
  6: { status: 'preparing', guests: 4, elapsedSeconds: 742, sessionId: 'session-6' },
}

export const demoTables: DiningTable[] = Array.from({ length: 12 }, (_, index) => {
  const n = index + 1
  const overrides: Partial<DiningTable> = demoTableOverrides[n] ?? { status: 'empty', guests: 0, elapsedSeconds: 0 }

  return {
    id: `table-${n}`,
    branchId: demoBranch.id,
    number: n,
    seats: n % 3 === 0 ? 6 : 4,
    status: 'empty',
    guests: 0,
    elapsedSeconds: 0,
    ...overrides,
  }
})

export const demoCartItems: CartItem[] = [
  { id: 'cart-1', sessionId: 'session-12', guestName: 'Joseph', menuItemId: 'item-burger', name: 'AVA Signature Burger', unitPrice: 4.9, quantity: 1 },
  { id: 'cart-2', sessionId: 'session-12', guestName: 'Joseph', menuItemId: 'item-cola', name: 'Cola', unitPrice: 1.2, quantity: 1 },
  { id: 'cart-3', sessionId: 'session-12', guestName: 'Ahmed', menuItemId: 'item-pasta', name: 'Truffle Rigatoni', unitPrice: 5.8, quantity: 1 },
  { id: 'cart-4', sessionId: 'session-12', guestName: 'Sara', menuItemId: 'item-salmon', name: 'Miso Glazed Salmon', unitPrice: 7.4, quantity: 1 },
]

const now = Date.now()
export const demoOrders: Order[] = [
  {
    id: 'order-127', restaurantId: demoRestaurant.id, branchId: demoBranch.id, tableId: 'table-12', tableNumber: 12,
    sessionId: 'session-12', status: 'new', createdAt: new Date(now - 38_000).toISOString(),
    items: demoCartItems.map((item, index) => ({ id: `oi-127-${index}`, orderId: 'order-127', guestName: item.guestName, name: item.name, quantity: item.quantity, unitPrice: item.unitPrice })),
  },
  {
    id: 'order-122', restaurantId: demoRestaurant.id, branchId: demoBranch.id, tableId: 'table-6', tableNumber: 6,
    sessionId: 'session-6', status: 'preparing', createdAt: new Date(now - 742_000).toISOString(), acceptedAt: new Date(now - 700_000).toISOString(), preparingAt: new Date(now - 660_000).toISOString(),
    items: [
      { id: 'oi-122-1', orderId: 'order-122', guestName: 'Guest 1', name: 'Charred Lemon Chicken', quantity: 2, unitPrice: 6.2 },
      { id: 'oi-122-2', orderId: 'order-122', guestName: 'Guest 2', name: 'Parmesan Fries', quantity: 1, unitPrice: 2.6 },
    ],
  },
  {
    id: 'order-120', restaurantId: demoRestaurant.id, branchId: demoBranch.id, tableId: 'table-4', tableNumber: 4,
    sessionId: 'session-4', status: 'ready', createdAt: new Date(now - 620_000).toISOString(), acceptedAt: new Date(now - 590_000).toISOString(), preparingAt: new Date(now - 540_000).toISOString(), readyAt: new Date(now - 68_000).toISOString(),
    items: [
      { id: 'oi-120-1', orderId: 'order-120', guestName: 'Guest 1', name: 'Miso Glazed Salmon', quantity: 1, unitPrice: 7.4 },
      { id: 'oi-120-2', orderId: 'order-120', guestName: 'Guest 2', name: 'Truffle Rigatoni', quantity: 1, unitPrice: 5.8 },
    ],
  },
]

export const demoRequests: ServiceRequest[] = [
  { id: 'req-7', sessionId: 'session-7', tableId: 'table-7', tableNumber: 7, type: 'bill', status: 'new', createdAt: new Date(now - 132_000).toISOString() },
  { id: 'req-9', sessionId: 'session-9', tableId: 'table-9', tableNumber: 9, type: 'assistance', status: 'new', createdAt: new Date(now - 224_000).toISOString(), message: 'Could someone help us with an allergy question?' },
]

export const demoMessages: ChatMessage[] = [
  { id: 'msg-1', sessionId: 'session-12', senderType: 'system', senderName: 'AVA XIN Delight', body: 'Your table session is active. The team is here if you need anything.', createdAt: new Date(now - 480_000).toISOString() },
  { id: 'msg-2', sessionId: 'session-12', senderType: 'guest', senderName: 'Joseph', body: 'Can we change one Cola to sparkling water?', createdAt: new Date(now - 100_000).toISOString() },
  { id: 'msg-3', sessionId: 'session-12', senderType: 'staff', senderName: 'AVA XIN Delight Team', body: 'Absolutely — we’ll take care of it.', createdAt: new Date(now - 80_000).toISOString() },
]
